import { useRef, useState, useEffect } from 'react';
import { IoChatboxEllipses } from 'react-icons/io5';
import { IoClose } from 'react-icons/io5';
import { twMerge } from 'tailwind-merge';

import { useDragAndSnap } from './hooks';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { extractAccessToken } from 'common/helpers/local-storage';
import { observer } from 'mobx-react';
import useStore from '../../../common/hooks/useStore';
import { useLoader } from 'common/hooks';
import Swal from 'sweetalert2';
import { arrGetCompanyApplicationMappingMain } from 'models/company/CompanyApplicationMapping';
import { getToken } from 'data/token/repositories/tokenRepository';
import { SpeechToTextInput } from './components';
import { Spinner } from '@chakra-ui/react';

type ChatMessage = {
    id: number;
    text: string;
    isUser: boolean;
    timestamp: string;
};

type GetMenuLog = {
    message: string;
    output: string;
};

type N8NResponse = {
    message: string;
    output: WebMenu[];
};

type WebMenu = {
    index: number;
    menu_path: string;
    menu_title: string;
    menu_subtitle: string;
    web_id: string;
};

// Add CSS animations for the full-screen transitions
const animationKeyframes = `
@keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes fadeOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.95); }
}

@keyframes slideIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

@keyframes slideOut {
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(20px); opacity: 0; }
}
`;

const F1Assistant = observer(() => {
    const { citizen_id } = extractAccessToken();

    const loader = useLoader();
    const { subDashboardStore, userApplicationStore, userCompanyStore } = useStore();
    const { selectedApplication } = subDashboardStore;
    const { userApplications } = userApplicationStore;
    const { citizenId, email, firstName, lastName } = userCompanyStore;

    const { data } = useQuery({
        queryKey: ['n8n'],
        queryFn: () => axios.get<{ n8n_api_url: string; fallback_message: string }>('/v1/n8n'),
        select: response => response.data,
        refetchOnWindowFocus: false
    });
    const n8nApiUrl = data?.n8n_api_url;
    const n8nFallbackMessage = data?.fallback_message;

    // References for the chat messages container and input field
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const assistantRef = useRef<HTMLDivElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [message, setMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [sending, setSending] = useState(false);
    const [menuResults, setMenuResults] = useState<WebMenu[]>();
    const { position, isDragging, handleMouseDown, handleTouchStart } =
        useDragAndSnap(assistantRef);

    const [transcripting, setTranscripting] = useState(false);

    const windowRef = useRef<ReturnType<typeof window.open>>();

    useEffect(() => {
        handleGetMenuLog();
    }, [citizen_id, n8nApiUrl]);

    const openFullScreen = () => {
        setIsFullScreen(true);
    };

    const closeFullScreen = () => {
        setIsExiting(true);
        // Add a small delay to allow the exit animation to play
        setTimeout(() => {
            setIsFullScreen(false);
            setIsExiting(false);
        }, 280);
    };

    const handleGetMenuLog = async () => {
        if (!citizen_id) {
            console.error('citizen_id is not defined');
            return;
        }

        if (!n8nApiUrl) {
            console.error('n8nApiUrl is not defined');
            return;
        }

        try {
            setSending(true);
            setIsTyping(true);

            // console.log('calling get log');
            const response = (
                await axios.post<GetMenuLog>(data.n8n_api_url, {
                    name: firstName,
                    sessionId: citizen_id,
                    chatInput: 'Get last 5 menu history'
                })
            ).data;

            // Web Menu main Workflow
            const assistantMessage = {
                id: 1,
                text: response.message || response.output,
                isUser: false,
                timestamp: new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                })
            };

            setChatMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            let fallbackMessage = '';
            if (error instanceof AxiosError) {
                fallbackMessage =
                    error.response?.data.message ||
                    n8nFallbackMessage ||
                    'เกิดข้อผิดพลาดในการดึงประวัติการใช้งานเมนู';
            }

            const assistantMessage = {
                id: 1,
                text: fallbackMessage || 'เกิดข้อผิดพลาดในการดึงประวัติการใช้งานเมนู',
                isUser: false,
                timestamp: new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                })
            };
            setChatMessages(prev => [...prev, assistantMessage]);
        } finally {
            setSending(false);
            setIsTyping(false);
        }
    };

    const handleSendMessage = async () => {
        if (!citizen_id) {
            console.error('citizen_id is not defined');
            return;
        }

        if (!n8nApiUrl) {
            console.error('n8nApiUrl is not defined');
            return;
        }

        if (!message.trim()) {
            return;
        }

        // Attempt to parse message as a number
        const parsedIndex = parseInt(message.trim(), 10);
        // Check if parsedIndex is a valid number and greater than 0
        const selectedIndex = !isNaN(parsedIndex) && parsedIndex > 0 ? parsedIndex : -1;
        // Determine the selected menu
        const selectedMenu = selectedIndex > 0 && menuResults ? menuResults[selectedIndex - 1] : [];

        // Add user message to chat
        const userMessage = {
            id: chatMessages.length + 1,
            text: message,
            isUser: true,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatMessages([...chatMessages, userMessage]);

        // Here you would typically send the message to your backend
        console.log('Sending message:', message);

        // Show typing indicator
        setIsTyping(true);
        // Clear the input after sending
        setMessage('');

        try {
            setSending(true);

            // console.log('calling get web menu path');
            const webMenu = (
                await axios.post<N8NResponse>(data.n8n_api_url, {
                    name: firstName,
                    sessionId: citizen_id,
                    chatInput: message.trim(),
                    selectedMenu: selectedMenu
                })
            ).data;

            // console.log('web menu path response', webMenu);

            if (Array.isArray(webMenu.output) && webMenu.output.length > 1) {
                setMenuResults(webMenu.output);

                const listedMenu = webMenu.output
                    .map(item => `${item.index}. ${item.menu_title} (${item.menu_subtitle})`)
                    .join('\n');

                const assistantMessage = {
                    id: chatMessages.length + 2,
                    text: `เราพบเมนู sale order หลายรายการ คุณต้องการเข้าใช้งานเมนูไหนคะ\n${listedMenu}`,
                    isUser: false,
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                };
                setChatMessages(prev => [...prev, assistantMessage]);

                // console.log('more than 1');
            } else if (Array.isArray(webMenu.output) && webMenu.output.length === 1) {
                // console.log('matched unique');

                const { web_id, menu_path, menu_title, menu_subtitle } = webMenu.output[0];

                const application = userApplications
                    .find(application => application.group_name === selectedApplication)
                    ?.application_list.find(application => application.web_id === web_id);

                if (application) {
                    setIsTyping(false);

                    const assistantMessage = {
                        id: chatMessages.length + 2,
                        text: `กำลังเปิด ${menu_title}`,
                        isUser: false,
                        timestamp: new Date().toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                    };
                    setChatMessages(prev => [...prev, assistantMessage]);

                    await openApplication(application, menu_path);

                    return;
                } else {
                    console.error('cannot find mathed web id and application');
                    return;
                }
            } else if (webMenu.message || webMenu.output.length > 0) {
                const assistantMessage = {
                    id: chatMessages.length + 2,
                    text: webMenu.message || (webMenu.output as unknown as string), // handle when sometime ai agent respond as message/output
                    isUser: false,
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                };

                setChatMessages(prev => [...prev, assistantMessage]);
            } else {
                const assistantMessage = {
                    id: chatMessages.length + 2,
                    text: `ขอโทษด้วย ฉันไม่เข้าใจคำสั่งนี้`,
                    isUser: false,
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                };

                setChatMessages(prev => [...prev, assistantMessage]);
            }
        } catch (error) {
            console.error(error);
            let fallbackMessage = '';
            if (error instanceof AxiosError) {
                fallbackMessage =
                    error.response?.data.message ||
                    n8nFallbackMessage ||
                    'เกิดข้อผิดพลาดระหว่างการติดต่อ';
            }

            const assistantMessage = {
                id: chatMessages.length + 2,
                text: fallbackMessage || 'เกิดข้อผิดพลาดระหว่างการติดต่อ',
                isUser: false,
                timestamp: new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                })
            };
            setChatMessages(prev => [...prev, assistantMessage]);
        } finally {
            setSending(false);
            setIsTyping(false);
        }
    };

    // Scroll to the bottom of the chat container when messages change
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages]);

    // Focus the input field when full-screen mode is activated
    useEffect(() => {
        if (isFullScreen && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300); // Small delay to ensure animation completes
        }
    }, [isFullScreen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const openApplication = async (
        application: arrGetCompanyApplicationMappingMain,
        menuPath: string
    ) => {
        try {
            loader.show();

            const isInactive = application.application_ingress === 'inactive';

            if (isInactive) {
                throw new Error('คุณไม่ได้รับอนุญาติให้ใช้แอปพลิเคชันนี้');
            }

            if (!application.company_token) {
                throw new Error('ไม่พบบริษัทนี้');
            }

            const userId = citizenId;
            const mode = localStorage.getItem('mode') || 'web';
            //if (companyToken != '1IbuASmpqIapjmhVfzcLF4XPVY9'
            //    && companyToken != '2KlP5DjCQeyOSgyAH0vNezHzu18') {
            //    userId = citizenId.split('@')[0];
            //}

            const [error, getTokenResponse] = await getToken({
                company_token: application.company_token,
                citizen_id: userId,
                email: email,
                first_name: firstName,
                last_name: lastName,
                mode: mode,
                sid: application.group_name,
                application: application.application_name.split('\n').join(' ')
            });

            if (error) {
                console.log(error);

                throw new Error(
                    'We are unable to process your request at this time. Please try again.'
                );
            }

            if (application.application_type == 'web') {
                const composedUrlWithToken =
                    application.application_ingress +
                    `?tgurl=${encodeURI(menuPath)}` +
                    `&token=${getTokenResponse}`;

                if (mode == 'gui') {
                    var jResWeb = {
                        action: 'LOGIN_SUCCESS',
                        token: getTokenResponse,
                        citizen_id: citizenId,
                        select_server: {
                            system_type: 'WEB',
                            //system_id: value.sid,
                            system_name: application.application_name,
                            //endpoint: value.ingress + '?token=' + result.token
                            endpoint: composedUrlWithToken
                            //host: value.host,
                            //port: value.port
                        }
                    };

                    var res = JSON.stringify(jResWeb);

                    (window as any).chrome.webview.postMessage(res);

                    return;
                }

                windowRef.current = window.open();
                windowRef.current && (windowRef.current.location = composedUrlWithToken);
            } else if (application.application_type == 'gui') {
                var jResGui = {
                    action: 'LOGIN_SUCCESS',
                    token: getTokenResponse,
                    citizen_id: citizenId,
                    select_server: {
                        system_type: 'GUI',
                        system_id: application.application_sid,
                        system_name: application.application_name.replace('\n', ' '),
                        host: application.application_host,
                        port: application.application_port
                    }
                };

                var res = JSON.stringify(jResGui);

                (window as any).chrome.webview.postMessage(res);
            } else {
                throw new Error(
                    'Application Type ' + application.application_type + ' does not support'
                );
            }
        } catch (error) {
            if (error instanceof Error) {
                // Swal.fire({
                //     title: '',
                //     text: error.message,
                //     icon: 'warning',
                //     confirmButtonColor: '#3085d6',
                //     confirmButtonText: 'OK',
                //     allowOutsideClick: false
                // });
                loader.hide();
                const result = await Swal.fire({
                    title: '',
                    text: error.message,
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Retry',
                    showCancelButton: true,
                    cancelButtonText: 'OK',
                    allowOutsideClick: false
                });

                if (result.isConfirmed) {
                    await openApplication(application, menuPath);
                }
            }
        } finally {
            loader.hide();
        }
    };

    const handleTranscript = (transcript: string) => {
        setMessage(transcript);
    };

    return (
        <>
            <style>{animationKeyframes}</style>
            {!isFullScreen ? (
                <div
                    ref={assistantRef}
                    // onMouseDown={handleMouseDown}
                    // onTouchStart={handleTouchStart}
                    style={{
                        left: `${position.x}px`,
                        top: `${position.y}px`,
                        opacity: isDragging ? 0.8 : 1,
                        transition: isDragging ? 'none' : 'all 0.2s ease-out'
                    }}
                    className={twMerge(
                        'flex h-10 w-10 items-center justify-center rounded-lg bg-primary-900 shadow-md',
                        'fixed z-[1500] cursor-grab active:cursor-grabbing'
                    )}
                    onClick={openFullScreen}
                >
                    <IoChatboxEllipses
                        size={24}
                        color="white"
                    />
                </div>
            ) : (
                <div
                    className="fixed z-[1500] flex flex-col bg-[#1C2F36] text-white transition-all duration-300 ease-in-out md:bottom-4 md:right-4 md:w-[400px] md:rounded-lg md:shadow-xl"
                    style={{
                        animation: isExiting
                            ? 'slideOut 0.3s ease-in-out'
                            : 'slideIn 0.3s ease-in-out',
                        height: window.innerWidth >= 768 ? '500px' : '100vh',
                        width: window.innerWidth >= 768 ? '400px' : '100%',
                        inset: window.innerWidth >= 768 ? 'auto' : 0
                    }}
                >
                    <div className="flex items-center justify-between p-4">
                        <h2 className="text-xl font-bold">F1 Assistant</h2>
                        <button
                            onClick={closeFullScreen}
                            className="rounded-full p-1 hover:bg-primary-800"
                        >
                            <IoClose size={24} />
                        </button>
                    </div>
                    <div
                        ref={chatContainerRef}
                        className="flex-1 overflow-auto bg-white p-4"
                        id="chat-messages"
                    >
                        {chatMessages.map(msg => (
                            <div
                                key={msg.id}
                                className={`mb-4 rounded-lg p-4 ${
                                    msg.isUser
                                        ? 'ml-auto max-w-[80%] bg-primary-800'
                                        : 'mr-auto max-w-[80%] bg-gray-400'
                                }`}
                            >
                                <p className="whitespace-pre-wrap">{msg.text}</p>
                                <div className="mt-1 text-right text-xs">{msg.timestamp}</div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="mb-4 mr-auto max-w-[80%] rounded-lg bg-primary-800 p-4">
                                <div className="flex space-x-2">
                                    <div className="h-3 w-3 animate-bounce rounded-full bg-white"></div>
                                    <div
                                        className="h-3 w-3 animate-bounce rounded-full bg-white"
                                        style={{ animationDelay: '0.2s' }}
                                    ></div>
                                    <div
                                        className="h-3 w-3 animate-bounce rounded-full bg-white"
                                        style={{ animationDelay: '0.4s' }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="bg-white p-4">
                        <div className="flex items-center gap-2">
                            <SpeechToTextInput
                                onTranscripting={setTranscripting}
                                onTranscript={handleTranscript}
                            />
                            <div className="relative flex-1">
                                <input
                                    disabled={sending || transcripting}
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Type your message ..."
                                    className="w-full rounded-lg border border-neutral-400 bg-white p-2 text-neutral-800 outline-none focus:ring-2 focus:ring-primary-500"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                                {transcripting && (
                                    <Spinner className="absolute right-2 top-2.5 text-primary-900" />
                                )}
                            </div>
                            <button
                                disabled={sending || transcripting}
                                className="rounded-lg bg-primary-900 px-4 py-2 font-medium hover:bg-primary-700"
                                onClick={handleSendMessage}
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

(F1Assistant as React.FC).displayName = 'F1Assistant';

export default F1Assistant;
